/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { ListViewProps } from '@airasia-phoenix/react-native/components/ListView';
import { useCallback, useState } from 'react';
import { isValidEmail, isValidPassword } from '../utils/validations';
import { useEmailDomainField } from './useEmailDomainField';

export type EmailAuthMessages = {
  required: string;
  emailInvalid: string;
  passwordInvalid: string;
};

type EmailErrorCode = Extract<
  keyof EmailAuthMessages,
  'required' | 'emailInvalid'
>;

type PasswordErrorCode = Extract<
  keyof EmailAuthMessages,
  'required' | 'passwordInvalid'
>;

export type EmailAuthCredentials = {
  email?: string;
  password?: string;
};

export interface UseEmailAuthFlowOptions {
  initialEmail?: string;
  initialPassword?: string;
  requirePassword: boolean;
  messages: EmailAuthMessages;
  onSubmit: (credentials: EmailAuthCredentials) => void;
  onEdit?: () => void;
}

/** Email + password field state and validation, shared by the login and sign-up forms. */
export function useEmailAuthFlow(options: UseEmailAuthFlowOptions) {
  const {
    initialEmail,
    initialPassword,
    requirePassword,
    messages,
    onSubmit,
    onEdit,
  } = options;

  const [emailErrorCode, setEmailErrorCode] = useState<EmailErrorCode | null>(
    null,
  );
  const clearEmailErrorCode = useCallback(() => setEmailErrorCode(null), []);

  const {
    email,
    emailErrorMessage: domainEmailErrorMessage,
    setHasEmailError,
    emailFieldRef,
    showEmailDomainDropdown,
    emailDomainDropdownData,
    emailDomainDropdownHeight,
    handleEmailChange: handleDomainEmailChange,
    handleEmailDomainSelect: handleDomainEmailSelect,
    dismissEmailDomainDropdown,
    handleEmailFocusChange,
    emailClearEndIcon,
    onEmailEndIconPress,
  } = useEmailDomainField({
    initialEmail: initialEmail?.trim() ?? '',
    invalidEmailMessage: messages.emailInvalid,
    onEmailFocus: clearEmailErrorCode,
  });

  const [password, setPassword] = useState(initialPassword);
  const [isSecure, setIsSecure] = useState(true);
  const [passwordErrorCode, setPasswordErrorCode] =
    useState<PasswordErrorCode | null>(null);

  // Derive from codes / flags + current messages so locale changes update live.
  const emailErrorMessage = emailErrorCode
    ? messages[emailErrorCode]
    : domainEmailErrorMessage;
  const passwordErrorMessage = passwordErrorCode
    ? messages[passwordErrorCode]
    : '';

  const handleEmailChange = useCallback(
    (text: string) => {
      // Mobile keyboards append a space after an autocompleted address, and
      // isValidEmail rejects any whitespace — so drop it on the way in.
      setEmailErrorCode(null);
      handleDomainEmailChange(text.trim());
      onEdit?.();
    },
    [handleDomainEmailChange, onEdit],
  );

  const handleEmailDomainSelect = useCallback(
    (item: ListViewProps) => {
      setEmailErrorCode(null);
      handleDomainEmailSelect(item);
      onEdit?.();
    },
    [handleDomainEmailSelect, onEdit],
  );

  const handleEmailClearPress = useCallback(() => {
    setEmailErrorCode(null);
    onEmailEndIconPress?.();
  }, [onEmailEndIconPress]);

  const handlePasswordChange = useCallback(
    (text: string) => {
      setPassword(text);
      setPasswordErrorCode(null);
      onEdit?.();
    },
    [onEdit],
  );

  const validate = useCallback(() => {
    const nextEmailErrorCode: EmailErrorCode | null = !email
      ? 'required'
      : isValidEmail(email)
        ? null
        : 'emailInvalid';
    setEmailErrorCode(nextEmailErrorCode);
    // Submit validation owns the message; clear blur-side flag to avoid stale overlap.
    setHasEmailError(false);

    if (!requirePassword) return !nextEmailErrorCode;

    const nextPasswordErrorCode: PasswordErrorCode | null = !password
      ? 'required'
      : null;
    setPasswordErrorCode(nextPasswordErrorCode);

    return !nextEmailErrorCode && !nextPasswordErrorCode;
  }, [email, password, requirePassword, setHasEmailError]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    onSubmit({ email, password });
  }, [validate, onSubmit, email, password]);

  return {
    email,
    password,
    emailErrorMessage,
    passwordErrorMessage,
    isSecure,
    setIsSecure,
    handleEmailChange,
    handlePasswordChange,
    handleSubmit,
    emailFieldRef,
    showEmailDomainDropdown,
    emailDomainDropdownData,
    emailDomainDropdownHeight,
    handleEmailDomainSelect,
    dismissEmailDomainDropdown,
    handleEmailFocusChange,
    emailClearEndIcon,
    onEmailEndIconPress: onEmailEndIconPress
      ? handleEmailClearPress
      : undefined,
  };
}
